import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, ArrowRight, Check } from "lucide-react";

const ServiceAreasPage = () => {
  const mainAreas = [
    { city: "Houston", desc: "All Houston neighborhoods including Downtown, Midtown, The Heights, River Oaks, Memorial, and more" },
    { city: "Katy", desc: "Including Cinco Ranch, Seven Lakes, Grand Lakes, and surrounding communities" },
    { city: "Sugar Land", desc: "Including First Colony, New Territory, Sweetwater, and surrounding areas" },
    { city: "The Woodlands", desc: "Including all villages and surrounding communities" },
    { city: "Pearland", desc: "Including Silverlake, Shadow Creek Ranch, and surrounding areas" },
    { city: "Cypress", desc: "Including Bridgeland, Towne Lake, Cypress Creek, and surrounding communities" },
  ];

  const additionalAreas = [
    "Spring", "Richmond", "Rosenberg", "Missouri City", "Stafford", "Bellaire",
    "West University Place", "Tomball", "Humble", "Atascocita", "Kingwood", "League City",
    "Friendswood", "Clear Lake", "Pasadena", "Baytown", "La Porte", "Seabrook",
    "Fulshear", "Brookshire", "Jersey Village", "Champions", "Willowbrook", "Copperfield"
  ];

  return (
    <div className="pt-20" data-testid="service-areas-page">
      {/* Hero Section */}
      <section className="bg-forest-950 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-emerald-400 font-accent font-medium uppercase tracking-wider text-sm">Service Areas</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mt-3 mb-6">
              Serving Greater Houston
            </h1>
            <p className="text-emerald-100/80 text-lg">
              Professional lawn care services across Houston and surrounding communities. Check if we service your area.
            </p>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="map-container h-96 rounded-2xl overflow-hidden shadow-lg">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d443086.5350818498!2d-95.68166854999999!3d29.8169295!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640b8b4488d8501%3A0xca0d02def365053b!2sHouston%2C%20TX!5e0!3m2!1sen!2sus!4v1699999999999!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Service Areas Map"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Main Service Areas */}
      <section className="section-padding bg-emerald-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-forest-950 mb-6">
              Primary Service Areas
            </h2>
            <p className="text-gray-600 text-lg">
              We provide comprehensive lawn care services in these major Houston-area communities.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mainAreas.map((area, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow border border-emerald-100"
                data-testid={`area-card-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-forest-950 mb-2">{area.city}</h3>
                    <p className="text-gray-600 text-sm">{area.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Areas */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-forest-950 mb-6">
              Additional Communities We Serve
            </h2>
            <p className="text-gray-600 text-lg">
              We also provide lawn care services to these surrounding areas.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3 max-w-5xl mx-auto">
            {additionalAreas.map((area, index) => (
              <span 
                key={index}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-800 rounded-full text-sm font-medium border border-emerald-100 hover:bg-emerald-100 transition-colors"
              >
                <Check className="w-4 h-4" />
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Not in List Section */}
      <section className="py-16 bg-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-forest-950 mb-4">
            Don't See Your Area Listed?
          </h2>
          <p className="text-gray-600 text-lg mb-8">
            We're always expanding our service area! Contact us to check if we can service your location.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-4"
                data-testid="areas-contact-btn"
              >
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+1-771-1871-987">
              <Button 
                variant="outline" 
                className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-full px-8 py-4"
                data-testid="areas-call-btn"
              >
                <Phone className="mr-2 w-5 h-5" />
                +1-771-1871-987
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-forest-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            Ready for Professional Lawn Care?
          </h2>
          <p className="text-emerald-100/80 text-lg mb-8">
            No matter where you are in the greater Houston area, we're ready to help you achieve the lawn of your dreams.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-emerald-500 hover:bg-emerald-400 text-white rounded-full px-8 py-6 text-lg shadow-xl"
              data-testid="areas-quote-btn"
            >
              Get Your Free Quote Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServiceAreasPage;
